import React, { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  TextField,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { ReactComponent as FayeIcon } from "../icons/FayeBrand.svg";
import AuthService from "services/auth";
import RsvpNoteDialog, {
  RsvpNoteDialogType,
} from "components/calendar/RsvpNoteDialog";
import { FayeAppBar } from "components/layout/NavigationLayout";
import LinkRouter from "components/navigation/LinkRouter";
import { isStandaloneWebapp } from "common/utils";
import { isValidEmail } from "common/userUtils";
import TwoFactorVerificationMethod from "components/auth/TwoFactorVerificationMethod";
import TwoFactorOTP from "components/auth/TwoFactorOTP";
import { isAxiosError } from "axios";

interface FormValues {
  username: string;
  password: string;
}

type LoginStep = "credentials" | "verification-method" | "otp";

const cleanPhone = (phone: string): string => {
  const clean = phone.replace(/[^\d+]/g, "");
  if (clean.length === 11 && clean[0] === "1") {
    return `+${clean}`;
  } else if (clean.length === 10) {
    return `+1${clean}`;
  } else {
    return clean;
  }
};

const isValidPhone = (phone: string) => {
  return /^\+1\d{10}$/.test(cleanPhone(phone));
};
export default ({ linkLogin = false }: { linkLogin?: boolean }) => {
  const navigate = useNavigate();
  const [rsvpDialogType, setRsvpDialogType] =
    useState<RsvpNoteDialogType>(null);
  const [sent, setSent] = useState(false);
  const isStandalone = isStandaloneWebapp();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const intendedPath = queryParams.get("destination");
  // Default to the value in the local state, then the intended path, then null.
  const redirectedFrom = location.state?.from?.pathname || intendedPath || null;
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<LoginStep>("credentials");
  const [twoFactorToken, setTwoFactorToken] = useState<string>("");
  const [selectedMethod, setSelectedMethod] = useState<"email" | "sms" | null>(
    null,
  );
  const [methodSelectionError, setMethodSelectionError] = useState<
    string | null
  >(null);
  const [formValues, setFormValues] = useState<FormValues>({
    username: "",
    password: "",
  });
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const email = urlParams.get("email");
    if (email) {
      setFormValues({ ...formValues, username: email });
    }
  }, []);

  useEffect(() => {
    const currentUser = AuthService.getCurrentUser();
    if (currentUser) {
      navigate(intendedPath || "/");
    }
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !loading && !sent) {
      event.preventDefault();
      const formEvent = {
        preventDefault: () => {},
      } as React.FormEvent<HTMLFormElement>;
      handleSubmit(formEvent);
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (
      !(isValidEmail(formValues.username) || isValidPhone(formValues.username))
    ) {
      setError("Please enter a valid email or phone number");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    if (!linkLogin) {
      AuthService.login(
        formValues.username,
        formValues.password,
        "member",
        null,
      ).then(
        (response) => {
          setLoading(false);

          if (!response) {
            return;
          }

          // Check if 2FA is required
          if (response.requiresTwoFactor && response.twoFactorToken) {
            setTwoFactorToken(response.twoFactorToken);
            setFormValues({
              ...formValues,
              password: "", // Clear password from state for security
            });
            setStep("verification-method");
          } else if (response.accessToken) {
            // Normal login succeeded (no 2FA required)
            // Send them back to the page they tried to visit when they were
            // redirected to the login page. Use { replace: true } so we don't create
            // another entry in the history stack for the login page.  This means that
            // when they get to the protected page and click the back button, they
            // won't end up back on the login page, which is also really nice for the
            // user experience.
            navigate(redirectedFrom, { replace: true });
          } else if (response.redirectUrl) {
            // Redirect case already handled by AuthService
            return;
          }
        },
        () => {
          setLoading(false);
          setError("Username or password is incorrect");
        },
      );
    } else {
      AuthService.initiateMemberLogin(
        isValidEmail(formValues.username) ? formValues.username : "",
        isValidPhone(formValues.username)
          ? cleanPhone(formValues.username)
          : "",
        redirectedFrom,
      ).then(
        () => {
          setSent(true);
          setLoading(false);
          setError(null);
        },
        (error) => {
          const resMessage =
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            error.message ||
            error.toString();
          setLoading(false);
          setError(resMessage);
        },
      );
    }
  };

  const handleSelectMethod = async (method: "email" | "sms") => {
    setSelectedMethod(method);
    setMethodSelectionError(null);

    try {
      await AuthService.initiateTwoFactor(twoFactorToken, method);
      setStep("otp");
    } catch (err) {
      if (isAxiosError(err)) {
        if (err.response?.status === 429) {
          setMethodSelectionError(
            "Too many attempts. Please try again in a few minutes.",
          );
        } else if (err.response?.data) {
          // Backend returns error message as plain string in response.data
          const message =
            typeof err.response.data === "string"
              ? err.response.data
              : err.response.data.message ||
                err.response.data.error ||
                "Failed to send code. Please try again.";
          setMethodSelectionError(message);
        } else {
          setMethodSelectionError("Failed to send code. Please try again.");
        }
      } else {
        setMethodSelectionError("Failed to send code. Please try again.");
      }
    }
  };

  const handleVerifyOTP = async (code: string) => {
    const response = await AuthService.verifyTwoFactor(
      twoFactorToken,
      code,
      null,
    );
    if (response.redirectUrl) {
      window.location.href = response.redirectUrl;
      return;
    }
    navigate(redirectedFrom, { replace: true });
  };

  const handleResendOTP = async () => {
    try {
      await AuthService.resendTwoFactorCode(twoFactorToken);
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 429) {
        throw new Error(
          "Too many attempts. Please try again in a few minutes.",
        );
      }
      throw err;
    }
  };

  const handleCancel = () => {
    setStep("credentials");
    setSelectedMethod(null);
    setError(null);
    setMethodSelectionError(null);
    setTwoFactorToken("");
  };

  const handleBackFromOTP = () => {
    setStep("verification-method");
    setSelectedMethod(null);
    setMethodSelectionError(null);
  };

  useEffect(() => {
    if (location.state?.calendarEvent?.action === "confirm") {
      setRsvpDialogType("confirm");
    }
    if (location.state?.calendarEvent?.action === "invalid") {
      setRsvpDialogType("invalid");
    }
  }, []);

  // Render 2FA verification method selection
  if (step === "verification-method") {
    return (
      <TwoFactorVerificationMethod
        onSelectMethod={handleSelectMethod}
        onCancel={handleCancel}
        error={methodSelectionError}
      />
    );
  }

  // Render 2FA OTP input
  if (step === "otp" && selectedMethod) {
    return (
      <TwoFactorOTP
        method={selectedMethod}
        onVerify={handleVerifyOTP}
        onCancel={handleBackFromOTP}
        onResend={handleResendOTP}
      />
    );
  }

  // Render credentials form (default)
  return (
    <>
      <FayeAppBar />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "500px",
            padding: "0px 28px",
            alignSelf: "stretch",
          }}
        >
          <div>
            <FayeIcon
              style={{
                width: "112.762px",
                height: "64px",
                flexShrink: "0",
                marginBottom: "29px",
              }}
            />
          </div>
          <Typography
            variant="h2Serif"
            sx={{
              marginBottom: "8px",
            }}
          >
            Sign in to Faye
          </Typography>
          {/*<Typography*/}
          {/*  variant="body"*/}
          {/*  sx={{*/}
          {/*    marginBottom: "32px",*/}
          {/*  }}*/}
          {/*>*/}
          {/*  Your Family Advisor awaits.*/}
          {/*</Typography>*/}
          {sent && (
            <Alert
              severity="success"
              sx={{
                marginBottom: "12px",
                marginTop: "24px",
                borderRadius: "12px",
                border: "2px solid #BEDDBB",
                background: "#FBFDFB",
                fontWeight: "500",
                color: "#3D7C37",
                alignSelf: "stretch",
                "& .MuiSvgIcon-root": {
                  color: "#468E3E",
                },
              }}
            >
              A link to sign in has been sent if there is a matching account.
              See you soon!
            </Alert>
          )}
          {error && (
            <Alert
              icon={false}
              severity="error"
              sx={{
                alignSelf: "stretch",
                marginBottom: "12px",
                borderRadius: "12px",
                border: "2px solid #FAD3D0",
                background: "#FFFBFA",
              }}
            >
              {error}
            </Alert>
          )}
          <form onSubmit={handleSubmit} style={{ width: "100%" }}>
            <FormControl fullWidth style={{ marginTop: 16 }} disabled={loading}>
              <TextField
                sx={{ flexGrow: 1 }}
                disabled={loading || sent}
                autoFocus
                label="Email or phone number"
                name="username"
                type="text"
                fullWidth
                autoComplete="off"
                variant="outlined"
                value={formValues.username}
                onChange={handleInputChange}
                onKeyDown={linkLogin ? handleKeyDown : undefined}
              />
            </FormControl>
            {!linkLogin && (
              <FormControl
                fullWidth
                style={{ marginTop: 16 }}
                disabled={loading || sent}
              >
                <TextField
                  sx={{ flexGrow: 1 }}
                  disabled={loading}
                  label="Password"
                  name="password"
                  type="password"
                  autoComplete="on"
                  value={formValues.password}
                  onChange={handleInputChange}
                />
              </FormControl>
            )}
            <Button
              disabled={loading || sent}
              type="submit"
              fullWidth
              style={{ marginTop: "12px" }}
            >
              Sign in
            </Button>
            {!sent && linkLogin && (
              <Box
                sx={(theme) => ({
                  padding: "12px",
                  marginTop: "16px",
                  border: `1px solid ${theme.palette.border} `,
                  borderRadius: "12px",
                })}
              >
                <Typography
                  variant="bodySmall"
                  sx={{ marginBottom: "8px", display: "inline" }}
                >
                  We'll send you a one time link for a password-free sign in. Or
                  you can{" "}
                  <LinkRouter inline to={`/member-login`}>
                    <Typography
                      variant="bodySmall"
                      sx={{
                        color: "primary.main",
                        cursor: "pointer",
                        display: "inline",
                      }}
                    >
                      sign in with a password instead
                    </Typography>
                  </LinkRouter>
                  .
                </Typography>
              </Box>
            )}
            {!sent && !linkLogin && (
              <Box
                sx={(theme) => ({
                  padding: "12px",
                  marginTop: "16px",
                  border: `1px solid ${theme.palette.border} `,
                  borderRadius: "12px",
                })}
              >
                <Typography
                  variant="bodySmall"
                  sx={{ marginBottom: "8px", display: "inline" }}
                >
                  Haven't set up a password yet?{" "}
                  <LinkRouter inline to={`/member-login/link`}>
                    <Typography
                      variant="bodySmall"
                      sx={{
                        color: "primary.main",
                        cursor: "pointer",
                        display: "inline",
                      }}
                    >
                      Sign in with a one time link and set up your password
                    </Typography>
                  </LinkRouter>
                  .
                </Typography>
              </Box>
            )}
            {!linkLogin && (
              <Box
                display={"flex"}
                flexDirection={"row"}
                justifyContent={"center"}
              >
                <LinkRouter
                  to={`/forgot-password${
                    formValues.username
                      ? `?username=${encodeURIComponent(formValues.username)}`
                      : ""
                  }`}
                >
                  <Button variant="text" sx={{ mt: 1, mr: 1 }}>
                    Forgot password?
                  </Button>
                </LinkRouter>
              </Box>
            )}
            {!isStandalone && (
              <>
                <Divider sx={{ marginTop: "24px", marginBottom: "24px" }} />
                <Box
                  display={"flex"}
                  flexDirection={"row"}
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  <Typography
                    variant="body"
                    sx={{ color: "text.secondary", marginRight: "4px" }}
                  >
                    Not a member?
                  </Typography>
                  <LinkRouter
                    useColorScheme={true}
                    inline={true}
                    to={`/signup?button=login-page`}
                  >
                    Join now
                  </LinkRouter>
                </Box>
              </>
            )}
          </form>
        </Box>
        <RsvpNoteDialog
          open={!!rsvpDialogType}
          onClose={() => setRsvpDialogType(null)}
          type={rsvpDialogType}
        />
      </Box>
    </>
  );
};
